import React from 'react';

import {controlStyle} from '../lib/formStyles.js';

// ============================================================================
// Form patterns (Design Constitution I13, I16, I17, I18, Q02, Q29)
// ----------------------------------------------------------------------------
//   AddRowSlot     I13: the neutral dashed '+ Add Feed' slot after the last
//                  row; green on hover/focus (index.html [data-add-row-slot])
//   RemoveRowButton I13: the gray × with a 44px target; no confirm (the caller
//                  offers Undo where the row was autosaved)
//   FormSection    I18/Q29: numbered Title Case sub-heading (gray circle) for
//                  long forms — 3+ sections or taller than one phone screen
//   InlineRename   I16: a pencil beside the text opens an inline field; Enter
//                  saves, Esc cancels just the edit; only a failure shows
//                  locally ('Not Saved — Retry', Q02)
//   GridCellInput  I17: a light cell input with a border on hover/focus; the
//                  column header is the label (with unit); rows never lift
//   RowSaveFailure I17/Q02: 'Not Saved — Retry' beside a row that failed;
//                  there is no local 'Saved'
// ============================================================================

export function AddRowSlot({label, onAdd}: {label: string; onAdd: () => void}) {
  return (
    <button
      type="button"
      className="wcf-clickable"
      data-add-row-slot="1"
      onClick={onAdd}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        minHeight: 44,
        padding: '10px 12px',
        border: '1.5px dashed var(--border-strong)',
        borderRadius: 12,
        background: 'transparent',
        color: 'var(--text-2)',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

export function RemoveRowButton({
  label = 'Remove row',
  onRemove,
  disabled = false,
}: {
  label?: string;
  onRemove: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="wcf-clickable"
      aria-label={label}
      data-remove-row="1"
      disabled={disabled}
      onClick={() => {
        if (!disabled) onRemove();
      }}
      style={{
        width: 44,
        height: 44,
        flexShrink: 0,
        border: 'none',
        borderRadius: 999,
        background: 'transparent',
        color: 'var(--label)',
        fontFamily: 'inherit',
        fontSize: 20,
        lineHeight: 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
      }}
    >
      ×
    </button>
  );
}

// The numbered heading alone, for forms laid out on one grid (place it with
// style={{gridColumn: '1/-1'}}).
export function FormSectionHeading({
  number,
  title,
  style,
}: {
  number: number;
  title: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <h3
      data-form-section-heading={number}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        margin: '6px 0 4px',
        fontSize: 15,
        fontWeight: 700,
        color: 'var(--ink)',
        ...style,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: 'var(--surface-2)',
          color: 'var(--text-2)',
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {number}
      </span>
      <span>{title}</span>
    </h3>
  );
}

export function FormSection({
  number,
  title,
  children,
  id,
}: {
  number: number;
  title: React.ReactNode;
  children: React.ReactNode;
  id?: string;
}) {
  return (
    <section id={id} data-form-section={number} style={{marginBottom: 22}}>
      <FormSectionHeading number={number} title={title} style={{margin: '0 0 12px'}} />
      {children}
    </section>
  );
}

export function RowSaveFailure({onRetry}: {onRetry: () => void}) {
  return (
    <span data-row-save-failure="1" role="alert" style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
      <span style={{color: 'var(--danger-ink)', fontSize: 13, fontWeight: 600}}>Not Saved</span>
      <button
        type="button"
        className="wcf-clickable"
        data-row-save-retry="1"
        onClick={onRetry}
        style={{
          minHeight: 44,
          padding: '0 6px',
          border: 'none',
          background: 'transparent',
          color: 'var(--danger-ink)',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 700,
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        Retry
      </button>
    </span>
  );
}

type GridCellInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string;
  onChange: (next: string) => void;
};

export function GridCellInput({value, onChange, style, ...rest}: GridCellInputProps) {
  return (
    <input
      {...rest}
      data-grid-cell="1"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%',
        minHeight: 40,
        padding: '8px 10px',
        border: '1px solid transparent',
        borderRadius: 10,
        background: 'transparent',
        fontFamily: 'inherit',
        fontSize: 15,
        color: 'var(--ink)',
        ...style,
      }}
    />
  );
}

type RenameState = 'view' | 'edit' | 'saving' | 'failed';

// The next state of an inline rename after a key in the field: Enter saves a
// changed, non-empty name; Esc (or Enter on an unchanged name) cancels.
export function renameKeyAction(key: string, draft: string, current: string): 'save' | 'cancel' | null {
  if (key === 'Escape') return 'cancel';
  if (key !== 'Enter') return null;
  const next = draft.trim();
  return next && next !== current ? 'save' : 'cancel';
}

export function InlineRename({
  value,
  onSave,
  label = 'Rename',
  children,
}: {
  value: string;
  onSave: (next: string) => Promise<unknown> | unknown;
  label?: string;
  children?: React.ReactNode;
}) {
  const [state, setState] = React.useState<RenameState>('view');
  const [draft, setDraft] = React.useState(value);
  const save = async (next: string) => {
    setState('saving');
    try {
      await onSave(next);
      setState('view');
    } catch {
      setState('failed');
    }
  };
  if (state === 'view') {
    return (
      <span data-inline-rename="view" style={{display: 'inline-flex', alignItems: 'center', gap: 4}}>
        {children ?? value}
        <button
          type="button"
          className="wcf-clickable"
          aria-label={label}
          data-inline-rename-edit="1"
          onClick={() => {
            setDraft(value);
            setState('edit');
          }}
          style={{
            width: 44,
            height: 44,
            border: 'none',
            borderRadius: 999,
            background: 'transparent',
            color: 'var(--text-2)',
            fontFamily: 'inherit',
            fontSize: 16,
            cursor: 'pointer',
          }}
        >
          ✎
        </button>
      </span>
    );
  }
  return (
    <span data-inline-rename={state} style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
      <input
        autoFocus
        aria-label={label}
        value={draft}
        disabled={state === 'saving'}
        aria-invalid={state === 'failed' || undefined}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          const action = renameKeyAction(e.key, draft, value);
          if (!action) return;
          e.preventDefault();
          // Esc cancels just this edit, never the dialog around it.
          e.stopPropagation();
          if (action === 'save') void save(draft.trim());
          else setState('view');
        }}
        style={{...controlStyle, width: 'auto', minWidth: 180}}
      />
      {state === 'failed' && <RowSaveFailure onRetry={() => void save(draft.trim())} />}
    </span>
  );
}
